import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { Button, StatusBadge } from '@medusajs/ui';
import { useEntitlements } from '@/contexts/EntitlementsContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  billingApi,
  SubscriptionPlan,
  BillingInterval,
  PlanSlug,
  FeatureKey,
} from '@/services/billingApi';
import { Loading } from '@/components/Loading';

interface BillingPlansProps {
  onSelectTab?: (tab: 'overview' | 'plans') => void;
}

const FEATURE_LABELS: Array<{ key: FeatureKey; label: string }> = [
  { key: 'pos', label: 'Core POS & Register Terminal' },
  { key: 'sync', label: 'Cloud & Offline Sync' },
  { key: 'inventory', label: 'Inventory & Stock Tracking' },
  { key: 'expenses', label: 'Expense Recording & Receipts' },
  { key: 'basicReports', label: 'Basic Sales Reports' },
  { key: 'exports', label: 'CSV & Spreadsheet Data Exports' },
  { key: 'purchaseOrders', label: 'Supplier Purchase Orders & Replenishment' },
  { key: 'onlineStore', label: 'Online Storefront Catalog' },
  { key: 'advancedReports', label: 'Advanced Profit & Loss & Margin Reports' },
  { key: 'onlineCheckout', label: 'Online Checkout & Digital Payments' },
  { key: 'rota', label: 'Staff Rota, Shift Scheduling & Leaves' },
  { key: 'payroll', label: 'Automated UK Payroll & PAYE Submissions' },
  { key: 'vatReturns', label: 'HMRC Making Tax Digital (MTD) VAT Returns' },
  { key: 'foodSafety', label: 'Food Safety & Hygiene Compliance Logs' },
  { key: 'training', label: 'Interactive Staff Training Hub' },
];

export const getPlanHighlights = (plan: SubscriptionPlan): string[] => {
  const f = plan.features || {};
  const highlights: string[] = [];

  if (typeof f.maxLocations === 'number' || typeof f.maxStaff === 'number') {
    const locText =
      typeof f.maxLocations === 'number'
        ? `${f.maxLocations} Store Location${f.maxLocations > 1 ? 's' : ''}`
        : null;
    const staffText =
      typeof f.maxStaff === 'number' ? `up to ${f.maxStaff} staff` : null;
    highlights.push([locText, staffText].filter(Boolean).join(', '));
  }

  if (typeof f.maxRegisters === 'number' || typeof f.maxProducts === 'number') {
    const regText =
      typeof f.maxRegisters === 'number'
        ? `${f.maxRegisters} Register${f.maxRegisters > 1 ? 's' : ''}`
        : null;
    const prodText =
      typeof f.maxProducts === 'number'
        ? `up to ${Number(f.maxProducts).toLocaleString()} products`
        : null;
    highlights.push([regText, prodText].filter(Boolean).join(', '));
  }

  for (const { key, label } of FEATURE_LABELS) {
    if (f[key] === true) {
      highlights.push(label);
    }
  }

  return highlights;
};

const BillingPlans: React.FC<BillingPlansProps> = () => {
  const { entitlements, plan: activePlan, loading: entitlementsLoading } = useEntitlements();
  const { user } = useAuth();
  const isOwner = user?.role === 'OWNER';

  const [interval, setInterval] = useState<BillingInterval>(
    entitlements?.subscription?.billingInterval || 'MONTHLY'
  );
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [processingSlug, setProcessingSlug] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (entitlements?.subscription?.billingInterval) {
      setInterval(entitlements.subscription.billingInterval);
    }
  }, [entitlements?.subscription?.billingInterval]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const data = await billingApi.listPlans();
        setPlans(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setErrorMsg(err.message || 'Could not load subscription plans.');
      } finally {
        setLoading(false);
      }
    };

    void fetchPlans();
  }, []);

  const subStatus = entitlements?.subscription?.status;
  const hasPaidSubscription =
    subStatus === 'ACTIVE' || subStatus === 'PAST_DUE' || subStatus === 'GRACE_PERIOD';
  const currentPlanSlug = hasPaidSubscription
    ? activePlan?.slug || entitlements?.subscription?.plan?.slug || null
    : null;
  const currentInterval = entitlements?.subscription?.billingInterval || 'MONTHLY';

  const handleSelectPlan = async (targetPlan: SubscriptionPlan) => {
    if (!isOwner) {
      setErrorMsg('Only the business owner can change subscription plans.');
      return;
    }

    try {
      setProcessingSlug(targetPlan.slug);
      setErrorMsg(null);
      const res = await billingApi.createCheckout({
        planSlug: targetPlan.slug as PlanSlug,
        interval,
      });

      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to start plan checkout. Please try again.');
      setProcessingSlug(null);
    }
  };

  if (loading || (entitlementsLoading && !entitlements)) {
    return (
      <div className="py-24 flex justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-10">
      {errorMsg && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs flex items-center justify-between">
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg(null)} className="text-rose-500 font-bold ml-2">
            ×
          </button>
        </div>
      )}

      {/* Interval Toggle */}
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="inline-flex items-center p-1 bg-ui-bg-subtle rounded-xl border border-ui-border-base">
          <button
            onClick={() => setInterval('MONTHLY')}
            className={`px-3.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              interval === 'MONTHLY'
                ? 'bg-ui-bg-base text-ui-fg-base shadow-2xs font-semibold'
                : 'text-ui-fg-muted hover:text-ui-fg-base'
            }`}
          >
            Monthly Billing
          </button>
          <button
            onClick={() => setInterval('YEARLY')}
            className={`px-3.5 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
              interval === 'YEARLY'
                ? 'bg-ui-bg-base text-ui-fg-base shadow-2xs font-semibold'
                : 'text-ui-fg-muted hover:text-ui-fg-base'
            }`}
          >
            <span>Annual Billing</span>
            <span className="bg-emerald-50 text-emerald-700 text-[10px] font-semibold px-1.5 py-0.2 rounded-full border border-emerald-200">
              2 Months Free
            </span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left items-stretch">
        {plans.map((planItem) => {
          const isCurrentPlan = Boolean(currentPlanSlug && planItem.slug === currentPlanSlug);
          const isSameInterval = currentInterval === interval;
          const isCurrent = isCurrentPlan && isSameInterval;
          const isPopular = planItem.slug === 'shop_plus';
          const isProcessing = processingSlug === planItem.slug;

          const priceMonthly = Number(planItem.priceMonthly);
          const priceYearly =
            planItem.priceYearly !== undefined && planItem.priceYearly !== null
              ? Number(planItem.priceYearly)
              : priceMonthly * 12;
          const displayPrice = interval === 'YEARLY' ? priceYearly : priceMonthly;
          const highlights = getPlanHighlights(planItem);

          return (
            <div
              key={planItem.slug}
              className={`relative flex flex-col bg-ui-bg-base rounded-xl border p-5 md:p-6 transition-all shadow-none ${
                isCurrentPlan
                  ? 'border-2 border-[#172e50]'
                  : isPopular
                  ? 'border-ui-border-interactive shadow-xs'
                  : 'border-ui-border-base hover:border-ui-border-strong'
              }`}
            >
              {isPopular && !isCurrentPlan && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-[#172e50] text-white text-[10px] font-semibold shadow-2xs">
                  Most Popular
                </div>
              )}

              {isCurrentPlan && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-emerald-700 text-white text-[10px] font-semibold shadow-2xs">
                  Current Plan
                </div>
              )}

              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-base font-semibold text-ui-fg-base">{planItem.name}</h3>
              </div>

              <p className="text-xs text-ui-fg-muted mb-4 min-h-[32px]">{planItem.description}</p>

              <div className="flex items-baseline gap-1.5 mb-5 pb-4 border-b border-ui-border-base">
                <span className="text-2xl font-bold text-ui-fg-base tabular-nums">£{displayPrice}</span>
                <span className="text-ui-fg-muted text-xs">
                  /{interval === 'YEARLY' ? 'yr' : 'mo'}
                </span>
                {interval === 'YEARLY' && (
                  <span className="text-[11px] text-emerald-700 font-medium ml-1">
                    (£{(displayPrice / 12).toFixed(2)}/mo)
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2.5 mb-6 flex-1">
                <p className="text-xs font-medium text-ui-fg-muted mb-1">Included features:</p>
                {highlights.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-xs text-ui-fg-subtle leading-tight">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="pt-2">
                {isCurrent ? (
                  <Button disabled size="small" variant="secondary" className="w-full text-xs">
                    Current Plan
                  </Button>
                ) : isOwner ? (
                  <Button
                    size="small"
                    variant={isPopular ? 'primary' : 'secondary'}
                    onClick={() => handleSelectPlan(planItem)}
                    disabled={isProcessing}
                    className="w-full text-xs"
                  >
                    {isProcessing ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <span>
                        {isCurrentPlan
                          ? `Switch to ${interval === 'YEARLY' ? 'Annual' : 'Monthly'}`
                          : hasPaidSubscription
                          ? `Switch to ${planItem.name}`
                          : `Choose ${planItem.name}`}
                      </span>
                    )}
                  </Button>
                ) : (
                  <div className="p-2 rounded-lg bg-ui-bg-subtle border border-ui-border-base text-xs text-ui-fg-muted flex items-center justify-center gap-1.5">
                    <ShieldAlert size={13} className="text-ui-fg-muted shrink-0" />
                    <span>Owner required to upgrade</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BillingPlans;
